import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import Container from "../../../components/container/container";
import { numberWithCommas } from "../../../components/helpers";
import PendingOrdersSlider from "../../../components/slider/PendingOrdersSlider";
import { PendingOrdersNav } from "../../../components/slider/PendingOrdersSlider";
import { restaurantOrderService } from "../../../services/restaurantOrder.service";
import { storeOrderService } from "../../../services/storeOrder.service";
import { getUserFromStorage } from "../../../utils/Auth";

const REQUEST_TIMEOUT = 10000; // 10s per request — fail fast
const RETRY_DELAY = 1500;
const MAX_PER_TYPE = 4;
// An order can still be on its way days after it was placed, e.g. past midnight
const LOOKBACK_DAYS = 7;

// The server is overloaded or unreachable, rather than rejecting this request
const isServerBusy = (error) => {
  const status = error?.response?.status;
  return !status || status === 429 || status >= 500;
};

// The courier can finish the delivery while the order itself still says "shipped"
const isDelivered = (order) =>
  ["delivered", "completed"].includes(order?.courier_orders?.[0]?.status);

const toList = (response) => {
  const list = response?.data?.data ?? response?.data;
  return Array.isArray(list) ? list : [];
};

// One retry for a busy server. Failures stay silent: this is a background
// section of the home page, so it just doesn't show what it couldn't load.
const fetchOrders = async (request, signal) => {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return toList(await request());
    } catch (error) {
      if (axios.isCancel(error) || !isServerBusy(error) || attempt === 1) {
        return [];
      }
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      if (signal.aborted) return [];
    }
  }
  return [];
};

export const PendingOrders = () => {
  const ordersRef = useRef(null);
  const userId = getUserFromStorage()?.id;

  // Kept local, not in redux: the profile order pages use the same redux keys
  // for their own filters, so those orders would show up here otherwise.
  const [pendingOrders, setPendingOrders] = useState([]);
  // trackingMap: { [orderId]: trackingData | null }
  const [trackingMap, setTrackingMap] = useState({});

  useEffect(() => {
    if (!userId) return;

    const controller = new AbortController();
    const { signal } = controller;
    const config = { signal, timeout: REQUEST_TIMEOUT };

    const load = async () => {
      const payload = {
        user_id: userId,
        status: "shipped",
        order_paid: 1,
        start_date: moment().subtract(LOOKBACK_DAYS, "days").format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
      };

      // Both lists first, so every order is only tracked once
      const [restaurantOrders, storeOrders] = await Promise.all([
        fetchOrders(
          () =>
            restaurantOrderService.filterMyRestaurantOrderAction(payload, 1, config),
          signal
        ),
        fetchOrders(
          () => storeOrderService.filterMyStoreOrders(payload, 1, config),
          signal
        ),
      ]);
      if (signal.aborted) return;

      const orders = [
        ...restaurantOrders
          .filter((order) => !isDelivered(order))
          .slice(0, MAX_PER_TYPE)
          .map((order) => ({ ...order, _type: "restaurant" })),
        ...storeOrders
          .filter((order) => !isDelivered(order))
          .slice(0, MAX_PER_TYPE)
          .map((order) => ({ ...order, _type: "store" })),
      ];
      setPendingOrders(orders);

      // One at a time so we don't blast the server
      for (let i = 0; i < orders.length; i++) {
        const order = orders[i];
        try {
          const res = await (order._type === "restaurant"
            ? restaurantOrderService.trackRestaurantOrder({ id: order.id }, config)
            : storeOrderService.trackStoreOrder({ id: order.id }, config));
          if (signal.aborted) return;
          const data = res?.data?.data || res?.data || null;
          setTrackingMap((prev) => ({ ...prev, [order.id]: data }));
        } catch (error) {
          if (signal.aborted) return;
          // Server is struggling: leave the remaining orders alone
          const skipped = isServerBusy(error) ? orders.slice(i) : [order];
          setTrackingMap((prev) => ({
            ...prev,
            ...Object.fromEntries(skipped.map((item) => [item.id, null])),
          }));
          if (isServerBusy(error)) return;
        }
      }
    };

    load();
    return () => controller.abort();
  }, [userId]);

  // A card waits for its tracking result, so a delivered order never flashes up.
  // If tracking failed (null) we can't tell, so the order still shows.
  const visibleOrders = pendingOrders.filter(
    (order) => order.id in trackingMap && !isDelivered(trackingMap[order.id])
  );

  if (visibleOrders.length === 0) return null;

  return (
    <Container>
      <div className="py-5 mt-4 sm:mt-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold capitalize">Pending Orders</h3>
          <div className="flex items-center gap-4">
            <div className="block">
              <PendingOrdersNav ordersRef={ordersRef} />
            </div>
          </div>
        </div>

        <div>
          <PendingOrdersSlider ordersRef={ordersRef}>
            {visibleOrders.map((order) => {
              const tracking = trackingMap[order?.id];
              const courier = tracking?.courier || tracking?.rider;
              const outletName =
                tracking?.outlet_name ||
                (order._type === "restaurant" ? "Restaurant order" : "Store order");
              const outletCity = tracking?.outlet_city || "";
              const deliveryCode =
                tracking?.courier_orders?.[0]?.short_code || "";
              const isRestaurant = order._type === "restaurant";
              const orderLink = isRestaurant
                ? `/track/${order?.id}`
                : `/track/store/${order?.id}`;

              return (
                <div
                  className="w-full bg-white dark:bg-darkBg rounded-lg mb-2 relative"
                  key={order?.id}
                >
                  <Link to={orderLink} className="block w-full">
                    <div className="p-5 border dark:border-gray-700 bg-white dark:bg-darkBg rounded-xl">
                      <div className="flex justify-between mb-6">
                        <div>
                          <p className="text-lg font-bold mb-0.5 capitalize">
                            {outletName}
                            {outletCity && ` - ${outletCity}`}
                          </p>
                          <p className="text-sm text-gray-500">
                            {moment(order?.created_at).format(
                              "Do MMM, YYYY. h:mm a"
                            )}
                          </p>
                        </div>
                        <h3 className="text-2xl font-semibold">
                          ₦{numberWithCommas(order?.order_amount)}
                        </h3>
                      </div>

                      {courier && (
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-sm font-bold">
                            {courier?.name?.charAt(0) || "C"}
                          </div>
                          <div>
                            <p className="text-sm font-semibold">
                              {courier?.name || "Courier assigned"}
                            </p>
                            {courier?.phone && (
                              <p className="text-xs text-gray-500">
                                {courier?.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {deliveryCode && (
                        <div className="flex justify-between items-center">
                          <div className="flex gap-1">
                            {String(deliveryCode)
                              .split("")
                              .map((digit, i) => (
                                <span
                                  key={i}
                                  className="bg-red-500 text-white w-10 h-10 flex items-center justify-center rounded-full text-lg font-bold"
                                >
                                  {digit}
                                </span>
                              ))}
                          </div>
                          <p className="font-bold text-lg">Delivery Code</p>
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
              );
            })}
          </PendingOrdersSlider>
        </div>
      </div>
    </Container>
  );
};
